import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { selectIsPreviewMode } from 'state/selectors';
import { RootState } from 'state/store';

import { getInstallmentPlans } from 'services';

interface UseInstallmentPlansQueryOptions {
  enabled?: boolean;
}

export const getInstallmentPlansQueryKey = (
  propertyId?: number,
  leaseTermId?: number,
  leaseStartWindowId?: string | number,
  companyApplicationId?: number,
) => ['installment-plans', propertyId, leaseTermId, leaseStartWindowId, companyApplicationId];

export const useInstallmentPlansQuery = ({
  enabled = true,
}: UseInstallmentPlansQueryOptions = {}) => {
  const isPreviewMode = useSelector(selectIsPreviewMode);
  const application = useSelector((state: RootState) => state.application);
  const leaseTerm = useSelector((state: RootState) => state.unitSelection?.leaseTerm);
  const { applicant, activeResponse } = application ?? {};
  const propertyId = applicant?.propertyId;
  const companyApplicationId =
    activeResponse?.companyApplicationId || applicant?.currentResponse?.companyApplicationId;

  const query = useQuery({
    queryKey: getInstallmentPlansQueryKey(
      propertyId,
      leaseTerm?.id,
      leaseTerm?.leaseStartWindowId,
      companyApplicationId,
    ),
    queryFn: () =>
      getInstallmentPlans(
        propertyId!,
        leaseTerm!.id,
        companyApplicationId!,
        leaseTerm?.leaseStartWindowId ? Number(leaseTerm.leaseStartWindowId) : undefined,
        activeResponse?.leaseIntervalId || undefined,
      ),
    enabled: Boolean(enabled && propertyId && leaseTerm && companyApplicationId && !isPreviewMode),
    retry: false,
    // A lease term or lease-start-window change changes this key. Keep successful
    // results reusable for page mounts with the same key, but mark them stale so
    // returning to a previously selected term refreshes its Billing data.
    staleTime: 0,
    // The evaluator is mounted by pages throughout the Viewer. Do not request
    // Billing again merely because a page mounts with an unchanged selection;
    // retry only a previously failed query. A changed query key still fetches.
    refetchOnMount: (queryState) => queryState.state.status === 'error',
  });

  return {
    ...query,
    companyApplicationId,
    isPreviewMode,
    leaseTerm,
  };
};
